import { CheckCircle, Clock, FileText, LogOut } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useCadastrosParciais } from "../../../hooks/useCadastrosParciais";
import {
  addProdutosAtendimento,
  createAtendimento,
} from "../../../services/atendimentoService";
// No topo do arquivo (imports)
import { useAuth } from "../../../hooks/useAuth";
import {
  getClientes,
  getProdutosByUser,
} from "../../../services/firestoreService";
import {
  CadastroParcialFormData,
  Cliente,
  Funcionario,
  Produto,
  ProdutoReserva,
} from "../../../types";
import InputSearch from "../../Common/InputSearch";
import InputSearchProducts from "../../Common/InputSearchProducts";
import "./CadastroMedidas.css";
import { formatCPF } from "../../../utils/validation";

interface CadastroMedidasProps {
  funcionario: Funcionario;
  onLogout: () => void;
  userId: string;
}

const CadastroMedidas: React.FC<CadastroMedidasProps> = ({
  funcionario,
  onLogout,
  userId,
}) => {
  // Estados para controle de atendimento
  const [atendimentoIniciado, setAtendimentoIniciado] = useState(false);
  const [atendimentoId, setAtendimentoId] = useState<string>("");
  const [clienteNomeAtendimento, setClienteNomeAtendimento] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [clienteNovo, setClienteNovo] = useState(true);
  // Remover uso do hook de clientes e criar estado local
  const [clientes, setClientes] = useState<Cliente[]>([]);

  const [formData, setFormData] = useState<CadastroParcialFormData>({
    clienteNome: "",
    clienteId: "",
    observacoes: "",
    codigoFuncionario: funcionario.codigo,
  });
  const [loading, setLoading] = useState(false);

  // Estados para produtos
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [produtosSelecionados, setProdutosSelecionados] = useState<
    ProdutoReserva[]
  >([]);
  const [showProdutoDropdown, setShowProdutoDropdown] =
    useState<boolean>(false);
  const produtoSearchRef = useRef<HTMLDivElement>(null);

  const { addCadastro } = useCadastrosParciais(userId, funcionario.codigo);
  // Carregar produtos disponíveis ao montar o componente
  useEffect(() => {
    let isMounted = true;

    const loadProdutos = async () => {
      try {
        if (!userId) {
          return;
        }

        const result = await getProdutosByUser(userId);
        if (isMounted && result.success && result.data) {
          setProdutos(result.data);
        } else if (isMounted) {
          console.error("❌ Erro ao carregar produtos:", result.error);
        }
      } catch (error) {
        if (isMounted) {
          console.error("❌ Exceção ao carregar produtos:", error);
        }
      }
    };

    loadProdutos();

    return () => {
      isMounted = false;
    };
  }, []); // Executar apenas uma vez na montagem

  // Novo: carregar clientes do admin (igual produtos)
  useEffect(() => {
    let isMounted = true;
    const loadClientes = async () => {
      try {
        if (!userId) return;
        const result = await getClientes(userId);
        if (isMounted && result.success && result.data) {
          setClientes(result.data);
        } else if (isMounted) {
          console.error("❌ Erro ao carregar clientes:", result.error);
        }
      } catch (error) {
        if (isMounted) {
          console.error("❌ Exceção ao carregar clientes:", error);
        }
      }
    };
    loadClientes();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  // Gerenciar dropdown de produtos (fechar ao clicar fora)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        produtoSearchRef.current &&
        !produtoSearchRef.current.contains(event.target as Node)
      ) {
        setShowProdutoDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };


const handleIniciarAtendimento = async (e: React.FormEvent) => {
  e.preventDefault();

  // Prevenir múltiplas submissões
  if (isProcessing || loading || atendimentoIniciado) {
    return;
  }

  // Determinar nome e ID do cliente baseado no tipo
  const nomeCliente = clienteNovo 
    ? clienteNomeAtendimento.trim() 
    : formData.clienteNome.trim();
  
  const clienteId = clienteNovo 
    ? undefined 
    : formData.clienteId;

  // Validação baseada no tipo de cliente
  if (clienteNovo && !clienteNomeAtendimento.trim()) {
    toast.error("Digite o nome do cliente para iniciar o atendimento", {
      toastId: "erro-nome",
    });
    return;
  }

  if (!clienteNovo && !formData.clienteId) {
    toast.error("Selecione um cliente cadastrado", {
      toastId: "erro-cliente-id",
    });
    return;
  }

  if (!nomeCliente) {
    toast.error("Nome do cliente é obrigatório", {
      toastId: "erro-nome-vazio",
    });
    return;
  }

  setIsProcessing(true);
  setLoading(true);

  try {
    // Montar dados do atendimento
    const atendimentoData: any = {
      clienteNome: nomeCliente,
      funcionarioId: funcionario.id || "",
      tipoAtendimento: "atendimento_escolher",
      observacoes: "Atendimento iniciado para cadastro de medidas",
    };

    // Adicionar clienteId apenas se existir (cliente cadastrado)
    if (clienteId) {
      atendimentoData.clienteId = clienteId;
    }

    // Criar atendimento no sistema
    const result = await createAtendimento(
      atendimentoData,
      funcionario.nome,
      userId
    );

    if (result.success && result.data) {
      const novoId = result.data.id || "";

      setAtendimentoId(novoId);
      setFormData((prev) => ({
        ...prev,
        clienteNome: nomeCliente,
        clienteId: clienteId || "",
      }));

      // Aguardar um pouco para garantir que o estado foi atualizado
      setTimeout(() => {
        setAtendimentoIniciado(true);
        toast.success(
          `Atendimento iniciado para ${nomeCliente}!`,
          { toastId: "sucesso-inicio" }
        );
      }, 100);
    } else {
      console.error("❌ Erro ao iniciar atendimento:", result.error);
      toast.error("Erro ao iniciar atendimento", { toastId: "erro-criar" });
      setIsProcessing(false);
    }
  } catch (error) {
    console.error("❌ Exceção ao iniciar atendimento:", error);
    toast.error("Erro ao iniciar atendimento", { toastId: "erro-exception" });
    setIsProcessing(false);
  } finally {
    setLoading(false);
  }
};
  //   // Verificar se o produto já foi selecionado
  //   const jaExiste = produtosSelecionados.some(
  //     (p) =>
  //       p.produtoId === produto.id &&
  //       p.produtoTamanho === produto.tamanho &&
  //       p.produtoCor === produto.cor
  //   );

  //   if (jaExiste) {
  //     const atualizados = produtosSelecionados.map((p) => {
  //       if (
  //         p.produtoId === produto.id &&
  //         p.produtoTamanho === produto.tamanho &&
  //         p.produtoCor === produto.cor
  //       ) {
  //         const maxQtd = Math.max(
  //           1,
  //           produtos.find((pr) => pr.id === produto.id)?.quantidade || 1
  //         );
  //         const nextQtd = Math.min((p.quantidade || 1) + 1, maxQtd);
  //         const unit = p.precoUnitario ?? produto.preco ?? 0;
  //         return {
  //           ...p,
  //           quantidade: nextQtd,
  //           precoUnitario: unit,
  //           precoTotal: unit * nextQtd,
  //         } as ProdutoReserva;
  //       }
  //       return p;
  //     });
  //     setProdutosSelecionados(atualizados);
  //   } else {
  //     const novo: ProdutoReserva = {
  //       produtoId: produto.id!,
  //       produtoTitulo: produto.titulo,
  //       produtoTamanho: produto.tamanho,
  //       produtoCor: produto.cor,
  //       produtoCodigoProduto: produto.codigoProduto,
  //       quantidade: 1,
  //       precoUnitario: produto.preco || 0,
  //       precoTotal: produto.preco || 0,
  //       produtoObservacoes: "",
  //     };
  //     setProdutosSelecionados([...produtosSelecionados, novo]);
  //   }

  //   setProdutoSearchTerm("");
  //   setShowProdutoDropdown(false);
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clienteNome.trim()) {
      toast.error("Nome do cliente é obrigatório");
      return;
    }

    setLoading(true);

    try {
      // 1. Observações do atendimento (sem misturar produtos)
      const observacoesCompletas = formData.observacoes;

      // 2. Salvar cadastro parcial (se necessário) — sem incluir produtos nas observações
      const dadosParaSalvar = {
        ...formData,
        observacoes: observacoesCompletas,
      };

      const result = await addCadastro(dadosParaSalvar);

      if (result.success) {
        // 3. Atualizar o atendimento com produtos (campo dedicado) e observações separadas
        if (atendimentoId) {
          // Atualizar atendimento com dados completos
          const updateResult = await addProdutosAtendimento(atendimentoId, {
            observacoes: observacoesCompletas,
            produtosSelecionados: produtosSelecionados.map((p) => ({
              id: p.produtoId || "",
              titulo: p.produtoTitulo,
              tamanho: p.produtoTamanho,
              cor: p.produtoCor,
              codigoProduto: p.produtoCodigoProduto || "",
              observacao: p.produtoObservacoes || "",
            })),
          });

          if (updateResult.success) {
            toast.success(
              "Produtos e observações salvos! A secretaria finalizará o atendimento.",
              { toastId: "sucesso-atualizar" }
            );
          } else {
            toast.error("Erro ao salvar produtos e observações");
          }
        }

        // 4. Limpar formulário e resetar atendimento
        setFormData({
          clienteNome: "",
          observacoes: "",
          clienteId: "",
          codigoFuncionario: funcionario.codigo,
        });
        setProdutosSelecionados([]);
        setAtendimentoIniciado(false);
        setAtendimentoId("");
        setClienteNomeAtendimento("");
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Erro ao salvar cadastro:", error);
      toast.error("Erro ao salvar cadastro");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    onLogout();
  };

  const handleClienteTipoChange = (tipo: "novo" | "existente") => {
    setClienteNovo(tipo === "novo");
    setFormData((prev) => ({
      ...prev,
      clienteNome: "",
      clienteId: "",
    }));
  };

  return (
    <div className="cadastro-medidas-container">
      {/* Header */}
      <div className="cadastro-medidas-header">
        <Container fluid>
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <div className="funcionario-info">
                  <h4 className="mb-0">Cadastro de Medidas</h4>
                  <p className="text-muted mb-0">
                    Funcionário: {funcionario.nome} ({funcionario.codigo})
                  </p>
                </div>
              </div>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-danger"
                onClick={handleLogout}
                className="d-flex align-items-center gap-2"
              >
                <LogOut size={18} />
                Sair
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Main Content */}
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col lg={8} xl={6}>
            {/* Tela de Iniciar Atendimento */}
            {!atendimentoIniciado ? (
              <Card className="cadastro-medidas-card">
                <Card.Header className="text-center bg-primary text-white">
                  <div className="cadastro-medidas-icon">
                    <Clock size={32} />
                  </div>
                  <h5 className="mb-0 mt-2">Iniciar Atendimento</h5>
                  <p className="mb-0" style={{ fontSize: "14px" }}>
                    Digite o nome do cliente para começar
                  </p>
                </Card.Header>

                <Card.Body>
                  <Alert variant="info">
                    <strong>Importante:</strong> Ao iniciar o atendimento, ele
                    ficará registrado na tela de atendimentos para controle da
                    secretaria.
                  </Alert>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Tipo de Cliente
                    </Form.Label>
                    <div className="d-flex gap-3">
                      <Form.Check
                        type="radio"
                        label="Cliente Novo"
                        name="tipoCliente"
                        checked={clienteNovo}
                        onChange={() => handleClienteTipoChange("novo")}
                      />
                      <Form.Check
                        type="radio"
                        label="Cliente Cadastrado"
                        name="tipoCliente"
                        checked={!clienteNovo}
                        onChange={() => handleClienteTipoChange("existente")}
                      />
                    </div>
                  </Form.Group>

                  <Form onSubmit={handleIniciarAtendimento}>
                    <Form.Group className="mb-4">
                      {/* <Form.Control
                        type="text"
                        placeholder="Digite o nome completo do cliente"
                        value={clienteNomeAtendimento}
                        onChange={(e) =>
                          setClienteNomeAtendimento(e.target.value)
                        }
                        required
                        disabled={loading}
                        className="form-control-custom"
                        autoFocus
                      /> */}
                      <Row>
                        <Col md={12}>
                          {/* Cliente */}
                          {clienteNovo ? (
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                Nome do Cliente *
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Digite o nome do cliente"
                                value={clienteNomeAtendimento}
                                onChange={(e) =>
                                  setClienteNomeAtendimento(e.target.value)
                                }
                                required
                              />
                            </Form.Group>
                          ) : (
                            <InputSearch
                              name="Nome do Cliente"
                              data={clientes}
                              size={12}
                              value={
                                formData.clienteId
                                  ? clientes.find(
                                      (c) => c.id === formData.clienteId
                                    ) || null
                                  : null
                              }
                              onChange={(cliente: Cliente) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  clienteId: cliente.id || "",
                                  clienteNome: cliente.nome || "",
                                }));
                              }}
                              onClear={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  clienteId: "",
                                }))
                              }
                              required
                              renderItem={(cliente) => (
                                <div key={cliente.id}>
                                  <div>
                                  {cliente.nome}
                                  </div>
                                  <div>
                                    {formatCPF(cliente.cpf)}
                                  </div>
                                </div>
                              )}
                            />
                          )}
                        </Col>
                      </Row>
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={
                          loading ||
                          isProcessing ||
                          atendimentoIniciado ||
                          (clienteNovo
                            ? !clienteNomeAtendimento.trim()
                            : !formData.clienteId)
                        }
                        className="btn-custom"
                      >
                        {loading || isProcessing ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Iniciando...
                          </>
                        ) : (
                          <>
                            <Clock size={18} className="me-2" />
                            Iniciar Atendimento
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            ) : (
              /* Formulário de Cadastro de Medidas - Exibido após iniciar atendimento */
              <Card className="cadastro-medidas-card ">
                <Card.Header className="text-center">
                  <div className="cadastro-medidas-icon">
                    <FileText size={32} />
                  </div>
                  <h5 className="mb-0 mt-2">Cadastro de Medidas</h5>
                  <p className="text-muted mb-0">
                    Cliente: {formData.clienteNome}
                  </p>
                  <small className="badge bg-success mt-2">
                    Atendimento em Andamento
                  </small>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <InputSearchProducts
                        produtos={produtos}
                        onSelectedChange={(items) =>
                          setProdutosSelecionados(items)
                        }
                        labelSelected="Produtos Selecionados"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="form-label-custom">
                        <FileText size={18} className="me-2" />
                        Observações
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        name="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        placeholder="Anote aqui as medidas do cliente, preferências, observações importantes, etc."
                        disabled={loading}
                        className="form-control-custom"
                        maxLength={250}
                      />
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        type="submit"
                        variant="success"
                        size="lg"
                        disabled={loading || !formData.clienteNome.trim()}
                        className="btn-custom"
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Finalizando...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={18} className="me-2" />
                            Finalizar Atendimento
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CadastroMedidas;
